/** Content field types available to models, entries and the SDK. */
export const FIELD_TYPES = [
  'text',
  'richtext',
  'longtext',
  'number',
  'boolean',
  'datetime',
  'image',
  'file',
  'link',
  'email',
  'phone',
  'color',
  'select',
  'multiselect',
  'json',
  'repeater',
  'group',
  'seo_title',
  'seo_description',
  'og_image',
  'canonical_url',
] as const;
export type FieldType = (typeof FIELD_TYPES)[number];

export const ENVIRONMENT_TYPES = ['development', 'staging', 'production'] as const;
export type EnvironmentType = (typeof ENVIRONMENT_TYPES)[number];

export const CONTENT_MODEL_KINDS = ['page', 'global', 'component'] as const;
export type ContentModelKind = (typeof CONTENT_MODEL_KINDS)[number];

export const VERSION_SOURCES = ['editor', 'api', 'restore', 'import', 'seed'] as const;
export type VersionSource = (typeof VERSION_SOURCES)[number];

/** Per-environment publication state derived for the UI. */
export const ENTRY_STATUSES = ['draft', 'published', 'changed', 'scheduled', 'unpublished'] as const;
export type EntryStatus = (typeof ENTRY_STATUSES)[number];

export const LICENCE_STATUSES = [
  'draft',
  'pending_payment',
  'active',
  'trial',
  'suspended',
  'expired',
  'cancelled',
  'revoked',
  'exhausted',
] as const;
export type LicenceStatus = (typeof LICENCE_STATUSES)[number];

export const LICENCE_KINDS = ['trial', 'subscription', 'perpetual', 'manual'] as const;
export type LicenceKind = (typeof LICENCE_KINDS)[number];

export const ACTIVATION_STATUSES = [
  'active',
  'deactivated',
  'revoked',
  'pending_domain_verification',
] as const;
export type ActivationStatus = (typeof ACTIVATION_STATUSES)[number];

export const ACTIVATION_SOURCES = ['dashboard', 'key', 'admin', 'remote_runtime'] as const;
export type ActivationSource = (typeof ACTIVATION_SOURCES)[number];

export const SUBSCRIPTION_STATUSES = [
  'trialing',
  'active',
  'past_due',
  'unpaid',
  'canceled',
  'incomplete',
  'incomplete_expired',
  'paused',
] as const;
export type SubscriptionStatus = (typeof SUBSCRIPTION_STATUSES)[number];

export const DOMAIN_VERIFICATION_METHODS = ['dns_txt', 'html_file'] as const;
export type DomainVerificationMethod = (typeof DOMAIN_VERIFICATION_METHODS)[number];

export const DOMAIN_VERIFICATION_STATUSES = ['unverified', 'pending', 'verified', 'failed', 'expired'] as const;
export type DomainVerificationStatus = (typeof DOMAIN_VERIFICATION_STATUSES)[number];

export const WEBHOOK_EVENTS = [
  'content.published',
  'content.unpublished',
  'content.scheduled',
  'entry.updated',
  'entry.restored',
  'media.uploaded',
  'media.deleted',
  'site.created',
  'site.updated',
  'licence.activated',
  'licence.deactivated',
  'licence.status_changed',
  'domain.verified',
] as const;
export type WebhookEvent = (typeof WEBHOOK_EVENTS)[number];

export const PLAN_LIMIT_KEYS = [
  'production_sites',
  'staging_envs_per_site',
  'dev_envs_per_site',
  'team_members',
  'media_storage_mb',
  'api_keys',
  'locales',
] as const;
export type PlanLimitKey = (typeof PLAN_LIMIT_KEYS)[number];

export const FEATURE_KEYS = [
  'visual_editor',
  'scheduled_publish',
  'webhooks',
  'offline_licence_files',
  'fingerprint_binding',
  'ip_allowlist',
  'multi_locale',
  'custom_roles',
  'audit_export',
  'priority_support',
] as const;
export type FeatureKey = (typeof FEATURE_KEYS)[number];

/** Runtime licence health, used by the degraded-mode policy table. */
export const RUNTIME_STATES = [
  'ok',
  'stale',
  'grace_exceeded',
  'suspended',
  'expired',
  'revoked',
  'invalid',
] as const;
export type RuntimeState = (typeof RUNTIME_STATES)[number];

export const BILLING_INTERVALS = ['month', 'year', 'one_time'] as const;
export type BillingInterval = (typeof BILLING_INTERVALS)[number];

export const PRICE_KINDS = ['base', 'addon_site'] as const;
export type PriceKind = (typeof PRICE_KINDS)[number];

export const ACTOR_TYPES = ['user', 'api_key', 'system', 'admin'] as const;
export type ActorType = (typeof ACTOR_TYPES)[number];
